<template>
  <section class="chat-search-panel">
    <div class="chat-search-panel-head">
      <h3>搜索对话内容</h3>
      <button class="chat-search-panel-close" type="button" @click="close" aria-label="关闭">×</button>
    </div>
    <div class="chat-search-panel-input-wrap">
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/>
        <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
      <input
        v-model="keyword"
        ref="input"
        class="chat-search-panel-input"
        type="text"
        placeholder="搜索..."
      />
    </div>
    <div class="chat-search-panel-results" ref="results" @scroll="onScroll">
      <div v-if="loading && !results.length" class="chat-search-panel-empty">
        <span>搜索中…</span>
      </div>
      <div v-else-if="!results.length" class="chat-search-panel-empty">
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/>
          <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
        <span>{{ emptyText }}</span>
      </div>
      <template v-else>
        <div class="chat-search-result-hint">{{ keyword ? '找到 ' + total + ' 条匹配消息' : '显示最近消息，输入关键词筛选' }}</div>
        <div
          v-for="m in results"
          :key="m.id"
          class="chat-search-result-item"
          :class="{ active: activeId === m.id }"
          @click="jump(m)"
        >
          <div class="chat-search-result-avatar" :style="avatarStyle(m)">
            <img v-if="m.avatarImage" :src="m.avatarImage" alt="" />
            <span v-else>{{ avatarChar(m) }}</span>
          </div>
          <div class="chat-search-result-body">
            <div class="chat-search-result-top">
              <span class="chat-search-result-name">{{ m.name }}</span>
              <span class="chat-search-result-time">{{ m.time }}</span>
            </div>
            <div class="chat-search-result-text" v-html="renderPreview(m)"></div>
          </div>
        </div>
        <div v-if="loading && results.length" class="chat-search-result-hint" style="text-align:center">加载中…</div>
        <div v-else-if="!hasMore && results.length" class="chat-search-result-hint" style="text-align:center;color:#c0c8d4">已加载全部</div>
      </template>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { stripMarkdown } from '@/utils/markdown'
import { solidFromColor } from '@/utils/avatarColor'
import { api } from '@/utils/api'

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/i
const ESC = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const PAGE_SIZE = 30

function pickField(obj, ...keys) {
  for (const k of keys) {
    if (obj == null) return ''
    if (obj[k] !== undefined && obj[k] !== null) return obj[k]
  }
  return ''
}

function formatTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  const pad = (n) => (n < 10 ? '0' + n : '' + n)
  if (sameDay) return pad(d.getHours()) + ':' + pad(d.getMinutes())
  const sameYear = d.getFullYear() === now.getFullYear()
  if (sameYear) return (d.getMonth() + 1) + '/' + d.getDate()
  return d.getFullYear() + '/' + (d.getMonth() + 1) + '/' + d.getDate()
}

export default {
  name: 'ChatSearchPanel',
  data() {
    return {
      keyword: '',
      activeId: '',
      results: [],
      total: 0,
      offset: 0,
      loading: false,
      hasMore: false
    }
  },
  created() {
    // Vue 2 不会把 _ 开头的 data 字段代理到实例，所以这里手动挂
    this._searchSeq = 0
    this._debounceTimer = null
  },
  computed: {
    ...mapState(['currentSessionKey']),
    ...mapGetters(['currentConversationId']),
    emptyText() {
      if (this.keyword) return '没有找到匹配的消息'
      return '暂无消息'
    }
  },
  watch: {
    currentConversationId(newId, oldId) {
      if (oldId && newId !== oldId) {
        this.$store.commit('SET_CHAT_SEARCH_OPEN', false)
      }
      this.keyword = ''
      this.activeId = ''
      this.results = []
      this.total = 0
    },
    currentSessionKey(newKey, oldKey) {
      if (oldKey && newKey !== oldKey) {
        this.$store.commit('SET_CHAT_SEARCH_OPEN', false)
      }
    },
    // 话题切换：清空旧结果按新话题重搜
    '$store.state.currentTopicKey'() {
      this.activeId = ''
      this.results = []
      this.total = 0
      this.offset = 0
      this.scheduleSearch(0)
    },
    keyword() { this.scheduleSearch() }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.input) this.$refs.input.focus()
    })
    this.scheduleSearch(0)
  },
  beforeDestroy() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
  },
  methods: {
    close() {
      this.$store.commit('SET_CHAT_SEARCH_OPEN', false)
    },
    scheduleSearch(delay) {
      if (this._debounceTimer) clearTimeout(this._debounceTimer)
      const d = delay === undefined ? 300 : delay
      this._debounceTimer = setTimeout(() => this.runSearch(true), d)
    },
    async runSearch(reset) {
      const seq = ++this._searchSeq
      if (reset) {
        this.offset = 0
        this.results = []
        this.total = 0
        this.hasMore = false
      }
      const kw = this.keyword.trim()
      const params = { limit: PAGE_SIZE, offset: this.offset }
      if (this.currentConversationId) params.conversationId = this.currentConversationId
      const topicId = this.$store.state.currentTopicKey
      if (topicId) params.topicId = topicId
      if (kw) params.keyword = kw
      this.loading = true
      console.log('[ChatSearch] request params:', JSON.parse(JSON.stringify(params)))
      try {
        const data = await api.searchMessages(params)
        console.log('[ChatSearch] response data:', JSON.stringify(data && { messageCount: (data.messages || []).length, total: data.total, hasMessages: Array.isArray(data.messages) }))
        if (seq !== this._searchSeq) {
          console.log('[ChatSearch] seq mismatch, abort. seq=', seq, '_searchSeq=', this._searchSeq)
          return
        }
        const list = (data && Array.isArray(data.messages)) ? data.messages : []
        const total = (data && typeof data.total === 'number') ? data.total : list.length
        console.log('[ChatSearch] messages.length:', list.length, 'total:', total)
        const mapped = list.map(raw => this.mapApiMessage(raw)).filter(Boolean)
        console.log('[ChatSearch] mapped:', mapped.length, 'sample:', mapped[0])
        this.results = this.results.concat(mapped)
        this.total = total
        this.offset += PAGE_SIZE
        this.hasMore = this.results.length < total
        console.log('[ChatSearch] AFTER assign, this.results.length =', this.results.length, 'total=', total, 'hasMore=', this.hasMore)
        // 如果还有更多但当前未填满滚动容器（无滚动条），自动续拉
        this.$nextTick(() => {
          const el = this.$refs.results
          if (this.hasMore && el && el.scrollHeight <= el.clientHeight + 8) {
            this.runSearch(false)
          }
        })
      } catch (e) {
        console.warn('[ChatSearchPanel] search failed:', e && e.message)
      } finally {
        this.loading = false
      }
    },
    mapApiMessage(raw) {
      if (!raw) return null
      const id = pickField(raw, 'id', 'messageId', 'message_id')
      if (!id) return null
      const body = raw.body || {}
      const text = pickField(body, 'text', 'rawText') || pickField(raw, 'content', 'text', 'message') || ''
      const sender = raw.sender || {}
      const senderName = pickField(sender, 'name') || pickField(raw, 'senderName', 'sender_name', 'agentName', 'agent_name')
      const senderAvatar = pickField(sender, 'avatarUrl')
      const senderColorRgb = pickField(sender, 'avatarColorRgb') || ''
      const senderType = pickField(sender, 'type') || ''
      const senderId = pickField(sender, 'id') || pickField(raw, 'senderId', 'sender_id', 'userId', 'user_id')
      const createdAt = pickField(raw, 'createdAt', 'created_at', 'sentAt', 'sent_at', 'timestamp')
      const messageType = pickField(raw, 'messageType', 'message_type', 'type', 'kind') || 'text'
      const myId = this.$store.state.currentUserId
      const isOutgoing = senderType === 'user' && senderId && myId && senderId === myId
      // 附件优先从 body.files 取，兼容 attachments / files
      const filesRaw = (Array.isArray(body.files) && body.files.length) ? body.files
        : (Array.isArray(raw.attachments) ? raw.attachments : (raw.files || []))
      const attachments = filesRaw.map(f => ({
        name: f.name || f.fileName || '',
        size: f.size || 0,
        url: f.url || '',
        mimeType: f.mimeType || f.mime_type || '',
        type: f.type || '',
        kind: (f.type === 'image' || (f.mimeType && /^image\//.test(f.mimeType))) ? 'image' : 'file'
      }))
      let displayText = text
      if (!displayText && attachments.length) {
        const first = attachments[0]
        displayText = first.name || (first.kind === 'image' ? '[图片]' : '[文件]')
      }
      return {
        id: String(id),
        text: displayText,
        rawText: text,
        name: senderName || (isOutgoing ? '我' : 'Agent'),
        time: formatTime(createdAt),
        side: isOutgoing ? 'right' : 'left',
        avatarImage: senderAvatar || null,
        avatarColorRgb: senderColorRgb,
        avatarText: (senderName || (isOutgoing ? '我' : 'A')).slice(0, 1),
        kind: messageType,
        attachments
      }
    },
    onScroll(e) {
      if (this.loading || !this.hasMore) return
      const el = e.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        this.runSearch(false)
      }
    },
    avatarChar(m) {
      if (m.avatarText) return m.avatarText.slice(0, 1)
      const name = m.name || (m.side === 'right' ? '我' : 'A')
      return name.slice(0, 1)
    },
    avatarStyle(m) {
      if (m.avatarImage) return { background: 'transparent' }
      const bg = solidFromColor(m.avatarColorRgb)
      return bg ? { background: bg } : {}
    },
    renderPreview(m) {
      let text = m.text || ''
      if (!text) return ''
      text = stripMarkdown(text)
      if (text.length > 120) text = text.substring(0, 120) + '...'
      let html = ESC(text)
      const k = this.keyword.trim()
      if (k) {
        const re = new RegExp(k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
        html = html.replace(re, (mm) => '<mark>' + ESC(mm) + '</mark>')
      }
      return html
    },
    async jump(m) {
      this.activeId = m.id
      const convId = this.currentConversationId
      const topicId = this.$store.state.currentTopicKey || undefined
      try {
        await this.$store.dispatch('jumpToMessage', { conversationId: convId, topicId, messageId: m.id })
      } catch (e) { /* ignore */ }
      this.$nextTick(() => {
        const el = document.getElementById('msg-' + m.id)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('highlight-flash')
        setTimeout(() => el.classList.remove('highlight-flash'), 2000)
      })
    }
  }
}
</script>
